import moment from 'moment';
import { AddressState, initialAddressState } from '../address';
import { resolveAddressIdentity } from '../address/address-merge';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import {
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../../models/low-level/notification-identifier.model';
import {
    NotificationStatusModel,
    NotificationStatusModelFromJson,
} from '../../models/low-level/notification-status.model';
import { centriCareContract, contractTypeLabel } from '../../models/low-level/notification.constants';
import { ODataNotification } from '../odata-types/notification.type';
import type { NotificationEquipmentState, NotificationTaskState } from './notification-entities.state';
import { StatusModel, StatusModelFromJson } from '../../models/low-level/status.model';
import { ConfigConstants } from '../../config/config-constants';
import { TimeUtilities } from '../../utilities/time-utilities';
import { Equipment } from '../../types/equipment';
import { Notification } from '../../types/notification';
import { NotificationStatus } from '../../types/notification-status';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import {
    createDefaultNotificationEntry,
    defaultNotificationStatusFields,
    NotificationCompletionState,
    NotificationState,
    NotificationStatusFields,
    NotificationStatusModelFields,
    NotificationStatusRecordState,
} from './notification-collection.state';
import { odataToNotificationState } from './odata-to-notification-state';
import { NotificationIdState } from '../utilities/notification-id.type';
import { NotificationStateUtilities } from '../utilities/notification-state-utlities';

/** Reduce a {@link NotificationIdentifierModel} to the three id fields the store keeps. */
export function notificationIdToState(notificationId: NotificationIdentifierModel): NotificationIdState {
    return { frontId: notificationId.frontId, qmNum: notificationId.qmNum, maNum: notificationId.maNum };
}

/** Rebuild a {@link NotificationIdentifierModel} from the three id fields the store keeps. */
export function stateToNotificationIdModel(id: NotificationIdState): NotificationIdentifierModel {
    return new NotificationIdentifierModel(id.frontId, id.qmNum, id.maNum);
}

/**
 * A converted payload with its notification already in entry form: the entry starts without any
 * recorded status — what the device knows about the notification is carried over separately, by
 * {@link carryOverStatusFields} — and the addresses still travel next to it, since only the caller
 * can fold them into the address slice (see `ConvertedNotification`).
 */
export type ConvertedNotificationEntry = Readonly<{
    entry: NotificationState;
    customerAddress: AddressState;
    addressSet: AddressState[];
}>;

/**
 * Convert one freshly-fetched payload into the entry the store holds, plus the addresses taken out
 * of it. All the conversion happens in `odataToNotificationState`; this only adds the entry's own
 * fields.
 *
 * @param odata the payload exactly as `NdpHeaderSet` sent it
 */
export function odataToEntry(odata: ODataNotification): ConvertedNotificationEntry {
    const converted = odataToNotificationState(odata);
    return {
        entry: createDefaultNotificationEntry(converted.notification),
        customerAddress: converted.customerAddress,
        addressSet: converted.addressSet,
    };
}

/**
 * The converted notification with its addresses placed in a collection the slice already holds: an
 * address that collection knows is taken over under its identity (see {@link resolveAddressIdentity}),
 * and the notification's own links are rewritten to that identity, so a freshly fetched notification
 * points at the very address entry the timesheet and every earlier notification are already using
 * instead of at a duplicate of it.
 *
 * The `AddressSet` rows are placed first and then count as known themselves, which is what lets the
 * customer address of a notification the `AddressSet` already describes resolve to that short,
 * hideable row — the form the timesheet's address picker expects.
 *
 * @param converted the freshly converted notification
 * @param known the addresses the slice holds
 */
export function resolveConvertedAddresses(
    converted: ConvertedNotificationEntry,
    known: readonly AddressState[],
): ConvertedNotificationEntry {
    const identities = new Map<string, string>();
    const addressSet: AddressState[] = [];

    converted.addressSet.forEach((address) => {
        const resolved = resolveAddressIdentity(address, [...addressSet, ...known]);
        identities.set(address.id, resolved.id);
        addressSet.push(resolved);
    });

    const customerAddress = resolveAddressIdentity(converted.customerAddress, [...addressSet, ...known]);
    identities.set(converted.customerAddress.id, customerAddress.id);

    return {
        entry: {
            ...converted.entry,
            customerAddressId: identities.get(converted.entry.customerAddressId) ?? converted.entry.customerAddressId,
            addressIds: converted.entry.addressIds.map((id) => identities.get(id) ?? id),
        },
        customerAddress,
        addressSet,
    };
}

/**
 * The entry with any missing field filled in from {@link defaultNotificationStatusFields} and, for
 * `retained`, from a fresh entry's default. Applied to hydrated entries, which may come from a cache
 * written before those fields moved onto the entry.
 *
 * @param entry
 */
export function withEntryDefaults(entry: NotificationState): NotificationState {
    const defaults = { retained: false, ...defaultNotificationStatusFields() };
    return { ...defaults, ...entry };
}

/**
 * The status fields of an entry on their own, i.e. everything about it that the backend payload
 * does not carry. Takes the fields rather than a whole entry, so that a caller holding only a
 * notification's recorded status can normalize it the same way.
 *
 * @param entry
 */
export function statusFieldsOf(entry: NotificationStatusFields): NotificationStatusFields {
    return {
        escalated: entry.escalated,
        cancelled: entry.cancelled,
        unread: entry.unread,
        paused: entry.paused,
        status: entry.status,
        cancellationConfirmed: entry.cancellationConfirmed,
        completed: entry.completed,
    };
}

/**
 * The freshly-fetched entries, each carrying the status the device recorded for that notification.
 * The counterpart of replacing the collection wholesale on every load: a notification the backend
 * still sends keeps what it has been through, and one it no longer sends drops out with its status,
 * which is what makes a separate "remove the status of gone notifications" step unnecessary.
 *
 * The one exception to that dropping is a notification whose confirmation was completed — see
 * {@link retainedCompletedEntries}, which the reducer applies on top of this.
 *
 * @param fresh the entries built from the fetched payloads
 * @param stored the entries currently in the slice
 */
export function carryOverStatusFields(
    fresh: readonly NotificationState[],
    stored: readonly NotificationState[],
): NotificationState[] {
    return fresh.map((entry) => {
        const previous = stored.find((candidate) =>
            NotificationStateUtilities.compare(candidate.notificationId, entry.notificationId),
        );
        return previous ? { ...entry, ...statusFieldsOf(previous) } : entry;
    });
}

/**
 * The stored entries the load has to keep although the backend did not send them again: the ones
 * whose confirmation was completed, marked `retained` so that only persistence and the completion
 * queries see them (see `NotificationState.retained`). Aging them out is not this function's
 * job — `removeExpiredCompletedNotifications` owns the retention period, so that the load itself
 * stays free of a clock.
 *
 * @param fresh the entries built from the fetched payloads
 * @param stored the entries currently in the slice
 */
export function retainedCompletedEntries(
    fresh: readonly NotificationState[],
    stored: readonly NotificationState[],
): NotificationState[] {
    return stored
        .filter(
            (entry) =>
                entry.completed &&
                !fresh.some((candidate) =>
                    NotificationStateUtilities.compare(candidate.notificationId, entry.notificationId),
                ),
        )
        .map((entry) => (entry.retained ? entry : { ...entry, retained: true }));
}

/**
 * The main equipment of a notification's `equipmentSet`, i.e. the one the backend flagged with
 * `MEIndicator`. Lives here rather than on the service so that no query logic sits in the service
 * layer.
 *
 * @param notification
 */
export function mainEquipmentFromNotificationState(notification: Notification): Equipment | undefined {
    return notification.equipmentSet.find((equipment) => equipment.mainEquipmentIndicator === ConfigConstants.sapTrue);
}

/**
 * The plain `Equipment` view of one stored equipment row. Shared by the selectors' equipment
 * queries and {@link entryToNotificationState}, so that both describe an equipment the same way.
 *
 * @param equipment
 */
export function equipmentToState(equipment: NotificationEquipmentState): Equipment {
    return {
        equnr: equipment.equipmentNumber,
        tidnr: equipment.technicalIdentificationNumber,
        matnr: equipment.materialNumber,
        sernr: equipment.serialNumber,
        equiShortDesc: equipment.shortDescription,
        division: equipment.division,
        mainEquipmentIndicator: equipment.mainEquipmentIndicator,
    };
}

/**
 * The addresses a notification links to, resolved. What every reader that needs more of an address
 * than its id works with — the customer address and, for the few that want them, the notification's
 * `AddressSet` entries.
 */
export type NotificationAddresses = Readonly<{
    /** The customer address, i.e. where the work is done. */
    customer: AddressState;
    /** The addresses from the notification's `AddressSet`. */
    addressSet: AddressState[];
}>;

/**
 * The addresses an entry's links stand for, looked up in the address collection. A customer address
 * that does not resolve falls back to an empty one: every notification the load delivers has its
 * address in the slice, so a miss is a data error rather than a case to model — the readers that can
 * report it (`NotificationNgrxService.addressesFor`, `selectCustomerAddressStates`) do so themselves.
 *
 * @param entry
 * @param addresses the address collection to resolve against
 */
export function resolveNotificationAddresses(
    entry: NotificationState,
    addresses: readonly AddressState[],
): NotificationAddresses {
    return {
        customer: addresses.find((address) => address.id === entry.customerAddressId) ?? initialAddressState,
        addressSet: entry.addressIds
            .map((id) => addresses.find((address) => address.id === id))
            .filter((address): address is AddressState => address !== undefined),
    };
}

/**
 * The customer's display name: the four name lines of the address the notification links to, joined
 * and trimmed. Reads the address rather than the notification, since the address slice is where
 * those lines live now.
 *
 * @param address the address `customerAddressId` resolved to
 */
export function customerNameOf(address: AddressState | undefined): string {
    return [address?.name1, address?.name2, address?.name3, address?.name4]
        .map((namePart) => namePart ?? '')
        .join(' ')
        .trim();
}

/**
 * The contact person's name, i.e. the part of `NotificationText` in front of the first `/` — the
 * field carries name and phone number in one, separated by that slash.
 *
 * @param notification
 */
export function contactNameOf(notification: NotificationState): string {
    return notification.notificationText.split('/')[0];
}

/**
 * The contact person's phone number, i.e. everything in `NotificationText` behind the first `/`. The
 * rest is joined back together, since a phone number may well contain a further slash.
 *
 * @param notification
 */
export function contactPhoneOf(notification: NotificationState): string {
    return notification.notificationText.split('/').slice(1).join('/');
}

/**
 * Whether the notification is remote support: remote work switched on, and `REMT` among the user
 * statuses the backend concatenated into `userStatus`.
 *
 * The config value is a parameter rather than a lookup, like in every converter here that needs one:
 * the caller reads `selectEnableRemoteWork` from the config slice, which keeps this pure and keeps
 * the setting from being frozen into a stored notification.
 *
 * @param notification
 * @param enableRemoteWork the `EnableRemoteWork` config value
 */
export function isRemoteSupportOf(notification: NotificationState, enableRemoteWork: boolean): boolean {
    return enableRemoteWork && notification.userStatus.includes(ConfigConstants.USER_STATUS.REMT);
}

/**
 * Whether the notification is assigned to an application specialist rather than a service engineer.
 *
 * @param notification
 * @param appsTaskCode the `AppsTaskCode` config value
 */
export function isApplicationSpecialistOf(notification: NotificationState, appsTaskCode: string): boolean {
    return notification.taskGroup === appsTaskCode;
}

/**
 * Whether PACE forms are available for the notification: forms switched on, and the backend counted
 * at least one.
 *
 * @param notification
 * @param disablePaceForms the `DisablePaceForms` config value
 */
export function isPaceFormsEnabledOf(notification: NotificationState, disablePaceForms: boolean): boolean {
    return !disablePaceForms && notification.formsCount > 0;
}

/**
 * The notification's main equipment, i.e. the one the backend flagged with `MEIndicator`, or
 * `undefined` when it sent none — which the card and the header report as a data error.
 *
 * @param notification
 */
export function mainEquipmentOf(notification: NotificationState): NotificationEquipmentState | undefined {
    return notification.equipments.find((equipment) => equipment.mainEquipmentIndicator === ConfigConstants.sapTrue);
}

/** The `mainEquipmentIndicator` value marking the *affected* (as opposed to the main) equipment. */
export const AFFECTED_EQUIPMENT_INDICATOR = 'S';

/**
 * The notification's affected equipment, or `undefined` when it has none.
 *
 * @param notification
 */
export function affectedEquipmentOf(notification: NotificationState): NotificationEquipmentState | undefined {
    return notification.equipments.find(
        (equipment) => equipment.mainEquipmentIndicator === AFFECTED_EQUIPMENT_INDICATOR,
    );
}

/**
 * Rows in the order their `position` puts them in. The slice keeps the order the payload had (see
 * `NotificationState`), so anything that has to present them in that order sorts here.
 *
 * @param rows
 */
function sortedByPosition<T extends { position: number }>(rows: readonly T[]): T[] {
    return [...rows].sort((first, second) => first.position - second.position);
}

/** One entry of a contract section: a label and the value the backend filed under it. */
export type NotificationContractEntry = Readonly<{
    label: string;
    value: string;
}>;

/** One section of the notification's contract information, with the entries it groups. */
export type NotificationContractSection = Readonly<{
    /** The section's heading, `''` for entries the backend sent before opening a section. */
    header: string;
    entries: NotificationContractEntry[];
}>;

/** The `value` of the contract row that opens a section rather than carrying an entry. */
const CONTRACT_SECTION_HEADER = 'CONTRACTHEADER';

/**
 * The notification's contract information as the sections the contract screen shows: the stored rows
 * are flat, and a row whose value is `CONTRACTHEADER` opens a section whose heading is that row's
 * label. Entries the backend sends before any such row go into a leading section without a heading.
 *
 * The derivation the legacy `ContractModel` did in its own `fromOData`, as a pure converter over the
 * stored rows — so nothing but the rows has to be stored.
 *
 * @param notification
 */
export function contractSectionsOf(notification: NotificationState): NotificationContractSection[] {
    const sections: Array<{ header: string; entries: NotificationContractEntry[] }> = [];
    sortedByPosition(notification.contracts).forEach((row) => {
        if (row.value === CONTRACT_SECTION_HEADER) {
            sections.push({ header: row.label, entries: [] });
            return;
        }
        if (sections.length === 0) {
            sections.push({ header: '', entries: [] });
        }
        sections[sections.length - 1].entries.push({ label: row.label, value: row.value });
    });
    return sections;
}

/**
 * Whether the notification is covered by a CentriCare contract, which the contract rows say through
 * their `Contract type` entry.
 *
 * @param notification
 */
export function isCentriCareContractOf(notification: NotificationState): boolean {
    return notification.contracts.some(
        (contract) =>
            contract.label === contractTypeLabel &&
            contract.value.toLowerCase().replace(/\s+/g, '').includes(centriCareContract),
    );
}

/**
 * Build the plain `Notification` state view of an entry, with `updatedStatus` overridden by the
 * caller-supplied effective status (which is derived from the entry rather than stored on it).
 * Model-free: everything it needs is either on the entry already or, for the customer address, in
 * the address slice.
 *
 * @param entry
 * @param effectiveStatus
 * @param customerAddress the address `entry.customerAddressId` resolved to
 */
export function entryToNotificationState(
    entry: NotificationState,
    effectiveStatus: NotificationStatus,
    customerAddress: AddressState,
): Notification {
    const now = moment().toISOString();
    return {
        identifier: { ...entry.notificationId },
        backendStatus: { statusCode: entry.backendStatusCode, startTime: now, endTime: now },
        updatedStatus: effectiveStatus,
        languageId: entry.languageId,
        notificationType: entry.notificationType,
        address: customerAddress,
        equipmentSet: sortedByPosition(entry.equipments).map(equipmentToState),
        emailSet: sortedByPosition(entry.emails).map((email) => email.faxOrEmail),
        functionalLocation: entry.functionalLocation,
    };
}

/**
 * Convert a notification's status fields to the {@link NotificationStatusModel} the legacy
 * notification-status callers work with, restoring `moment` objects for the status timestamps via
 * `NotificationStatusModelFromJson`.
 *
 * @param notificationId the notification the status belongs to
 * @param fields its status fields
 */
export function notificationStatusFieldsToModel(
    notificationId: NotificationIdState,
    fields: NotificationStatusModelFields,
): NotificationStatusModel {
    return new NotificationStatusModelFromJson({
        notificationId,
        escalated: fields.escalated,
        cancelled: fields.cancelled,
        unread: fields.unread,
        paused: fields.paused,
        status: fields.status.map((status) => ({
            statusCode: status.statusCode as NOTIFICATION_STATUS_CODE,
            startTime: status.startTime,
            endTime: status.endTime,
        })),
    });
}

/**
 * {@link notificationStatusFieldsToModel} for a stored entry.
 *
 * @param entry
 */
export function entryToNotificationStatusModel(entry: NotificationState): NotificationStatusModel {
    return notificationStatusFieldsToModel(entry.notificationId, statusFieldsOf(entry));
}

/**
 * Convert a {@link NotificationStatusModel} back to the entry id it belongs to and the status
 * fields to apply to it, turning `moment` timestamps into ISO strings. Only the fields the model
 * carries: the cancellation confirmation and the completion record have no place on it, and a write
 * coming from a model must not clear them.
 *
 * @param model
 */
export function notificationStatusModelToFields(model: NotificationStatusModel): {
    id: NotificationIdState;
    fields: NotificationStatusModelFields;
} {
    return {
        id: notificationIdToState(model.notificationId),
        fields: {
            escalated: model.escalated,
            cancelled: model.cancelled,
            unread: model.unread,
            paused: model.paused,
            status: model.status.map((status) => ({
                statusCode: status.statusCode,
                startTime: status.startTime.toISOString(),
                endTime: status.endTime.toISOString(),
            })),
        },
    };
}

/**
 * The completed notifications among `entries`, in the {@link NotificationCompletedListModel} the
 * legacy callers work with — keyed by notification key, which is what the map this replaced was
 * keyed by.
 *
 * @param entries the entries to collect the completion records from, retained ones included
 */
export function completedNotificationsToListModel(
    entries: readonly NotificationState[],
): NotificationCompletedListModel {
    const completed: NotificationCompletedListModel['completedNotifications'] = {};
    entries.forEach((entry) => {
        if (!entry.completed) {
            return;
        }
        // The legacy model keys its record by `NotificationIdentifierModel.toString()`, so the
        // identifier the record carries is what produces the key it is stored under.
        const id = new NotificationIdentifierModelFromJson(entry.notificationId);
        completed[id.toString()] = {
            id,
            status: entry.completed.status as NOTIFICATION_STATUS_CODE,
            timestampCompleted: entry.completed.timestampCompleted,
        };
    });
    return new NotificationCompletedListModel(completed, 0);
}

/**
 * Convert a {@link NotificationCompletedListModel} back to the completion record of each entry it
 * names. The counterpart of {@link completedNotificationsToListModel}, for the legacy bulk write.
 *
 * @param model
 */
export function completedListModelToCompletions(
    model: NotificationCompletedListModel,
): Array<{ id: NotificationIdState; completion: NotificationCompletionState }> {
    return Object.values(model.completedNotifications).map((entry) => ({
        id: notificationIdToState(entry.id),
        completion: { status: entry.status, timestampCompleted: entry.timestampCompleted },
    }));
}

/**
 * Convert a {@link NotificationStatus} (or the equivalent stored status record) to a {@link StatusModel}.
 *
 * @param status
 */
export function statusStateToModel(status: NotificationStatus): StatusModel {
    return new StatusModelFromJson(status);
}

/**
 * Convert a {@link StatusModel} to the plain `NotificationStatus` shape, turning `moment` timestamps into ISO strings.
 *
 * @param status
 */
export function statusModelToState(status: StatusModel): NotificationStatus {
    return status.getStatusState();
}

/**
 * Convert a {@link StatusModel} to the serializable status record the entry stores, turning
 * `moment` timestamps into ISO strings.
 *
 * @param status
 */
export function statusModelToStatusRecord(status: StatusModel): NotificationStatusRecordState {
    return {
        statusCode: status.statusCode,
        startTime: status.startTime.toISOString(),
        endTime: status.endTime.toISOString(),
    };
}

/**
 * Convert a stored status record (`{ statusCode: string, ... }`) to the stricter
 * `NotificationStatus` shape (`statusCode: NOTIFICATION_STATUS_CODE`). The stored shape types
 * `statusCode` more loosely, so this is a narrowing, not a real conversion.
 *
 * @param status
 */
export function statusRecordToStatus(status: NotificationStatusRecordState): NotificationStatus {
    return {
        statusCode: status.statusCode as NOTIFICATION_STATUS_CODE,
        startTime: status.startTime,
        endTime: status.endTime,
    };
}

/**
 * The task for `code` with the highest `taskNumber`, i.e. the most recent revision the backend sent
 * for that code, or `undefined` when it sent none.
 *
 * Only the notification's tasks are consulted: the fallback to the entry's recorded statuses and
 * the ETA duration math are query concerns and live in `selectStatusForCodeById`.
 *
 * @param tasks the notification's tasks
 * @param code
 */
export function latestTaskForCode(
    tasks: readonly NotificationTaskState[],
    code: string,
): NotificationTaskState | undefined {
    const matches = tasks.filter((task) => task.statusCode === code);
    if (matches.length <= 1) {
        return matches[0];
    }
    return [...matches].sort((a, b) => (parseInt(a.taskNumber, 10) || 0) - (parseInt(b.taskNumber, 10) || 0)).at(-1);
}

/**
 * The planned-start moment of a task.
 *
 * @param task
 */
export function taskPlannedStart(task: NotificationTaskState): moment.Moment {
    return TimeUtilities.momentFromDateAndTime(task.plannedStartDate, task.plannedStartTime);
}

/**
 * The planned-end moment of a task.
 *
 * @param task
 */
export function taskPlannedEnd(task: NotificationTaskState): moment.Moment {
    return TimeUtilities.momentFromDateAndTime(task.plannedEndDate, task.plannedEndTime);
}

/**
 * The serializable status a task stands for, in the shape the entry records. Both timestamps are
 * truncated to the minute, since that is the resolution statuses are compared and displayed at.
 *
 * @param task the task
 */
export function taskToStatusRecord(task: NotificationTaskState): NotificationStatusRecordState {
    return {
        statusCode: task.statusCode,
        startTime: taskPlannedStart(task).startOf('minute').toISOString(),
        endTime: taskPlannedEnd(task).startOf('minute').toISOString(),
    };
}

/**
 * The notification's start moment computed from its tasks alone (DURA, else ETA, else DISP, else the
 * planned start the backend sent), without the fallback to the entry's recorded statuses that
 * `selectNotificationStartById` additionally applies. Used by the reducer to sort entries, and by
 * `selectEntriesSortedByStartDate` for the same reason. Backend payloads populate the task set for
 * essentially every notification, so the resulting order matches the full one in practice.
 *
 * @param entry
 */
export function notificationStartFromTasks(entry: NotificationState): moment.Moment {
    const dura = latestTaskForCode(entry.tasks, NOTIFICATION_STATUS_CODE.DURA);
    if (dura) {
        return taskPlannedStart(dura);
    }
    const etaOrDisp =
        latestTaskForCode(entry.tasks, NOTIFICATION_STATUS_CODE.ETA) ??
        latestTaskForCode(entry.tasks, NOTIFICATION_STATUS_CODE.DISP);
    if (etaOrDisp) {
        return taskPlannedStart(etaOrDisp);
    }
    return TimeUtilities.momentFromDateAndTime(entry.plannedStartDate, entry.plannedStartTime);
}

/**
 * Sort entries by {@link notificationStartFromTasks}, ascending. Used by the reducer only — see its docs.
 *
 * @param entries
 */
export function sortEntriesByStart(entries: NotificationState[]): NotificationState[] {
    return [...entries].sort(
        (a, b) => notificationStartFromTasks(a).valueOf() - notificationStartFromTasks(b).valueOf(),
    );
}

/**
 * Collapses an ETA status onto its start time before it is stored. The backend creates its ETA task
 * with `endTime` equal to the start datetime the user sent via the travel-status-update service, so
 * what is saved locally has to match that shape — the displayed arrival window is derived from it
 * later, in `selectStatusForCodeById`.
 *
 * A pure function rather than an `on()` handler, because it normalizes the argument of a status
 * write rather than the state the write produces.
 *
 * @param status
 */
/**
 * The record for a status the app is recording now.
 *
 * The model-based path built a `StatusModel` with the same instant for both bounds and then ran it
 * through {@link normalizeStatusForSave}, whose one rule — an ETA's end follows its start — that
 * already satisfies. So this is the same record with no model in between.
 *
 * @param statusCode the status being recorded
 * @param now the instant it is recorded at, as an ISO timestamp
 * @returns the record `addStatusToNotificationEntry` carries
 */
export function statusRecordFor(statusCode: NOTIFICATION_STATUS_CODE, now: string): NotificationStatusRecordState {
    return { statusCode, startTime: now, endTime: now };
}

export function normalizeStatusForSave(status: StatusModel): StatusModel {
    if (status.statusCode !== NOTIFICATION_STATUS_CODE.ETA) {
        return status;
    }
    return new StatusModel(status.statusCode, status.startTime, status.startTime.clone());
}
